import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withStyles, Box, BodyText, Skeleton } from 'vopay';
import { Title, Button, Card } from 'UI';
import { useDialog } from '@/providers/Dialog.provider';
import { useToast } from '@/providers/Toast.provider';
import AddSendingDomainDialog from './AddSendingDomainDialog';
import SendingDomainCard from './SendingDomainCard';
import useRemoveDomainDialog from './useRemoveDomainDialog';
import useEmailDomain from './useEmailDomain';
import noSendingDomainIcon from './noSendingDomain.svg';
import styles from './EmailDomainPage.module.scss';

const cn = withStyles(styles);

export default function EmailDomainPage() {
  const { t } = useTranslation('translations', { keyPrefix: 'Settings.EmailDomain' });

  const { fetching, verifying, deleting, domain, setDomain, fetchDomain, addDomain, verifyDomain, deleteDomain } =
    useEmailDomain();
  const confirmRemoveDomain = useRemoveDomainDialog();
  const dialog = useDialog();
  const toast = useToast();

  const [fetchFailed, setFetchFailed] = useState(false);

  useEffect(() => {
    fetchDomain()
      .then(() => setFetchFailed(false))
      .catch(() => setFetchFailed(true));
  }, []);

  function openAddDialog() {
    dialog.open<void>(
      ({ onConfirm, onCancel }) => (
        <AddSendingDomainDialog
          onConfirm={onConfirm}
          onCancel={onCancel}
          onSubmit={async (newDomain) => {
            const res = await addDomain({ Domain: newDomain });
            setDomain(res.Domain);
            toast.success(t('domainAdded', { defaultValue: 'Domain Added' }));
          }}
        />
      ),
      { bare: true, closeOnEscape: true },
    );
  }

  async function handleVerify() {
    if (!domain) return;

    try {
      const res = await verifyDomain({ Domain: domain.Domain });
      setDomain(res.Domain);

      if (res.Domain.Status === 'verified') {
        toast.success(t('domainVerified', { defaultValue: 'Domain Verified' }));
      } else {
        toast.neutral(t('domainCouldNotBeVerified', { defaultValue: "Domain couldn't be verified" }));
      }
    } catch {
      toast.neutral(
        t('verifyError', { defaultValue: 'Could not verify {{domain}}. Please try again.', domain: domain.Domain }),
      );
    }
  }

  async function handleRemove() {
    if (!domain) return;

    const confirmed = await confirmRemoveDomain(domain.Domain);

    if (!confirmed) return;

    try {
      await deleteDomain({ Domain: domain.Domain });
      setDomain(null);
      toast.success(t('domainDeleted', { defaultValue: 'Domain Deleted' }));
    } catch {
      toast.neutral(
        t('deleteError', { defaultValue: 'Could not remove {{domain}}. Please try again.', domain: domain.Domain }),
      );
    }
  }

  return (
    <Box flx="col" gapSize="40px">
      <Box flx="col" gapSize="8px">
        <Title level="1">{t('pageTitle', { defaultValue: 'Email Domain' })}</Title>

        <BodyText className={cn('description')}>
          {t('description', {
            defaultValue:
              'Add domain names or email addresses on this page to Send emails that land in the inbox from them. Sender domains allow you to send from any email addresses at a domain and are verified via DNS records.',
          })}
        </BodyText>
      </Box>

      <Box flx="col" gapSize="20px">
        {!domain && (
          <Box flx="row" jc="fe">
            <Button
              colorScheme="primary"
              size="large"
              iconLeft="add"
              disabled={fetching || fetchFailed}
              onClick={openAddDialog}
            >
              {t('newDomainButton', { defaultValue: 'New domain' })}
            </Button>
          </Box>
        )}

        {domain && <Title level="4">{t('sendingDomainTitle', { defaultValue: 'Sending domain' })}</Title>}

        {domain ? (
          <SendingDomainCard
            domain={domain}
            busy={verifying || deleting}
            onVerify={handleVerify}
            onRemove={handleRemove}
          />
        ) : (
          <Card className={cn('card')}>
            {fetching ? (
              <Skeleton height={56} count={4} className={cn('rowSkeleton')} />
            ) : (
              <Box flx="col" ai="center" jc="center" gapSize="8px">
                <img src={noSendingDomainIcon} alt="" className={cn('emptyStateIcon')} />
                <Title level="3">{t('EmptyState.title', { defaultValue: 'No sending domain yet' })}</Title>

                <div className={cn('emptyStateDescription')}>
                  {fetchFailed
                    ? t('fetchError', { defaultValue: 'Could not load your sending domain.' })
                    : t('EmptyState.description', {
                        defaultValue:
                          "Verify the domain your emails are sent from and we'll authenticate every email sent on your behalf, so they reach the inbox instead of spam",
                      })}
                </div>
              </Box>
            )}
          </Card>
        )}
      </Box>
    </Box>
  );
}
